// Chapter-as-landscape state machine.
//
// The essay is one long walk from pre-dawn (I. Waiting) to open horizon
// (V. Question). The current "active" chapter drives:
//   - html[data-chapter] attribute → CSS hooks for accent tone, rail glow
//   - a `chapter:change` CustomEvent → shaders subscribe to shift palette
//
// Detection rule: the chapter whose <section> has the greatest overlap
// with the viewport's middle band wins. Using a plain IntersectionObserver
// with a tight rootMargin around the vertical center gives us this cheaply
// — whichever section's top/bottom straddles the center line is active.

export type ChapterKey = 'hero' | 'I' | 'II' | 'III' | 'IV' | 'V' | 'mechanics' | 'footer';

export interface ChapterChangeDetail {
  from: ChapterKey;
  to: ChapterKey;
}

let active: ChapterKey = 'hero';

export function currentChapter(): ChapterKey {
  return active;
}

export function initChapterState(): void {
  const sections = Array.from(
    document.querySelectorAll<HTMLElement>('section[data-chapter]')
  );
  if (!sections.length) return;

  // Tight band around the vertical center — whichever section is straddling
  // the center line is the "active" one. rootMargin pulls the detection zone
  // to a ~10vh slice around middle.
  const io = new IntersectionObserver(
    (entries) => {
      // Pick the entry closest to the viewport center.
      let bestEntry: IntersectionObserverEntry | null = null;
      let bestDistance = Infinity;
      const viewportMid = window.innerHeight / 2;

      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        const rect = entry.boundingClientRect;
        const sectionMid = rect.top + rect.height / 2;
        const distance = Math.abs(sectionMid - viewportMid);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestEntry = entry;
        }
      }

      if (bestEntry) {
        const next = (bestEntry.target as HTMLElement).dataset.chapter as ChapterKey;
        if (next && next !== active) {
          const prev = active;
          active = next;
          document.documentElement.dataset.chapter = next;
          document.dispatchEvent(
            new CustomEvent<ChapterChangeDetail>('chapter:change', {
              detail: { from: prev, to: next },
            })
          );
        }
      }
    },
    {
      // Narrow center band — detect when the section's bulk is at viewport mid.
      rootMargin: '-45% 0px -45% 0px',
      threshold: 0,
    }
  );

  sections.forEach((s) => io.observe(s));

  // Also observe every time scroll settles so the initial page load picks up
  // whichever chapter is visible at reload (IO fires initial entries, but
  // setting the attribute up front avoids a frame of "unset" state).
  const initial = sections.find((s) => {
    const r = s.getBoundingClientRect();
    return r.top <= window.innerHeight / 2 && r.bottom >= window.innerHeight / 2;
  });
  if (initial) {
    active = (initial.dataset.chapter as ChapterKey) ?? 'hero';
    document.documentElement.dataset.chapter = active;
  } else {
    document.documentElement.dataset.chapter = 'hero';
  }
}
